import { atom } from 'jotai';

// --- TYPES ---

export type MaterialSortingRules = {
  salesOrderItemIds: Set<string>;
  requestedItemIds: Set<string>;
};

/**
 * Explicit modal modes that define the user's intent and view behavior:
 * - 'add': Adding a new catalytic converter item (RootView)
 * - 'change': Changing which item is selected for an existing row (RootView with highlight)
 * - 'edit': Editing details for an existing item (DetailView, locked to item)
 */
export type CatalyticModalMode = 'add' | 'change' | 'edit';

export type CatalyticModalState = {
  isOpen: boolean;
  /** Explicit mode defining user intent */
  mode: CatalyticModalMode;
  /** The inventory item ID to highlight in root view or show details in edit mode */
  selectedItemId: string | null;
  /** The line item (row) ID to update when user picks a new item */
  lineItemId: string | null;
};

export type MaterialsModalMode = 'add' | 'change';
export type MaterialsModalItemType = 'items' | 'tags';

export type MaterialsModalState = {
  isOpen: boolean;
  mode: MaterialsModalMode;
  itemType: MaterialsModalItemType;
  selectedItemId: string | null;
  lineItemId: string | null;
};

export type WeightFieldType = 'gross' | 'tare';

// 🔘🔘🔘 PLAIN ATOMS - Row focus tracking for staged grid
export const $rowFocusState = atom<Map<string, boolean>>(new Map());

// 🔘🔘🔘 PLAIN ATOMS - Last focused row ID, persists after blur (for "Take Weight" button)
export const $lastFocusedRowId = atom<string | null>(null);

// 🔘🔘🔘 PLAIN ATOMS - Last focused weight field type (gross/tare), persists after blur
export const $lastFocusedWeightFieldType = atom<WeightFieldType | null>(null);

// 🔘🔘🔘 PLAIN ATOMS - Sorting rules for material select
export const $materialSortingRules = atom<MaterialSortingRules>({
  salesOrderItemIds: new Set<string>(),
  requestedItemIds: new Set<string>(),
});

// 🔘🔘🔘 PLAIN ATOMS - Row ID to auto-focus after adding a new row
export const $autoFocusRowId = atom<string | null>(null);

// 🔘🔘🔘 PLAIN ATOMS - Load header bounding rect for positioning
export const $loadHeaderRect = atom({
  width: 0,
  height: 0,
  left: 0,
  top: 0,
  bottom: 0,
  right: 0,
});

// 🔘🔘🔘 PLAIN ATOMS - Catalytic modal open/mode/selection state
export const $catalyticModalState = atom<CatalyticModalState>({
  isOpen: false,
  mode: 'add',
  selectedItemId: null,
  lineItemId: null,
});

// 🔘🔘🔘 PLAIN ATOMS - Materials modal open/mode/selection state
export const $materialsModalState = atom<MaterialsModalState>({
  isOpen: false,
  mode: 'add',
  itemType: 'items',
  selectedItemId: null,
  lineItemId: null,
});

// --- DERIVED DATA ---

// 🟢🟢🟢 DERIVED DATA -> Currently focused row ID from focus state map
export const $focusedRowId = atom<string | null>((get) => {
  const focusState = get($rowFocusState);
  for (const [rowId, isFocused] of focusState.entries()) {
    if (isFocused) return rowId;
  }
  return null;
});

// 🟠🟠🟠 ACTION -> SET row focus state (clears other rows when focusing)
export const $setRowFocus = atom(
  null,
  (get, set, { rowId, isFocused }: { rowId: string; isFocused: boolean }) => {
    const currentState = get($rowFocusState);
    const newState = new Map<string, boolean>();

    if (isFocused) {
      newState.set(rowId, true);
      set($lastFocusedRowId, rowId);
    } else {
      currentState.forEach((value, key) => {
        if (key !== rowId) {
          newState.set(key, value);
        }
      });
      newState.set(rowId, false);
    }

    set($rowFocusState, newState);
  },
);

// 🟠🟠🟠 ACTION -> CLEAR focus state for a specific row
export const $clearRowFocus = atom(null, (get, set, rowId: string) => {
  const currentState = get($rowFocusState);
  const newState = new Map(currentState);
  newState.delete(rowId);
  set($rowFocusState, newState);
});

// 🟠🟠🟠 ACTION -> OPEN catalytic modal with mode and selection
export const $openCatalyticModal = atom(
  null,
  (
    _,
    set,
    params: {
      mode: CatalyticModalMode;
      selectedItemId?: string | null;
      lineItemId?: string | null;
    },
  ) => {
    const { mode, selectedItemId = null, lineItemId = null } = params;
    set($catalyticModalState, {
      isOpen: true,
      mode,
      selectedItemId,
      lineItemId,
    });
  },
);

// 🟠🟠🟠 ACTION -> CLOSE catalytic modal and reset state
export const $closeCatalyticModal = atom(null, (_, set) => {
  set($catalyticModalState, {
    isOpen: false,
    mode: 'add',
    selectedItemId: null,
    lineItemId: null,
  });
});

// 🟠🟠🟠 ACTION -> OPEN materials modal with mode and item type
export const $openMaterialsModal = atom(
  null,
  (
    _,
    set,
    params: {
      mode: MaterialsModalMode;
      itemType: MaterialsModalItemType;
      selectedItemId?: string | null;
      lineItemId?: string | null;
    },
  ) => {
    const { mode, itemType, selectedItemId = null, lineItemId = null } = params;
    set($materialsModalState, {
      isOpen: true,
      mode,
      itemType,
      selectedItemId,
      lineItemId,
    });
  },
);

// 🟠🟠🟠 ACTION -> CLOSE materials modal and reset state
export const $closeMaterialsModal = atom(null, (_, set) => {
  set($materialsModalState, {
    isOpen: false,
    mode: 'add',
    itemType: 'items',
    selectedItemId: null,
    lineItemId: null,
  });
});
